import type { FC } from 'react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';

import type { DetailedFollowUpTicket } from 'src/types/followUp';

import ModalSection from '../ModalSection';

import ChangelogComment from './ChangelogComment';
import ChangelogUpdate from './ChangelogUpdate';
import { styles } from './styles';
import type { ChangelogEntry, CommentEntry } from './types';

interface ChangelogSectionProps {
  ticket: DetailedFollowUpTicket;
  extended: boolean;
  toggleExtended: () => void;
}

const ChangelogSection: FC<ChangelogSectionProps> = ({
  ticket,
  extended,
  toggleExtended,
}) => {
  const { t } = useTranslation('followUp');

  /**
   * An array of changes and comments to the ticket, sorted by date descending.
   */
  const entries: (ChangelogEntry | CommentEntry)[] = useMemo(
    () =>
      [
        ...ticket.changelog.map<ChangelogEntry>((change) => ({
          ...change,
          actionType: 'change',
          comparisonTime:
            change.timestamp === undefined
              ? 0 // If undefined, will always be at the end.
              : Date.parse(change.timestamp),
        })),

        ...ticket.comments.map<CommentEntry>((comment) => ({
          ...comment,
          actionType: 'comment',
          comparisonTime: Date.parse(comment.createdAt),
        })),
      ].sort(
        // Sort by unix timestamp descending.
        (prev, next) => next.comparisonTime - prev.comparisonTime,
      ),
    [ticket],
  );

  return (
    <ModalSection
      header={t('changelog.name')}
      extendable={{
        extended,
        toggleExtended,
      }}
    >
      <Box sx={styles.changelogContainer}>
        {entries.map((entry) =>
          entry.actionType === 'comment' ? (
            <ChangelogComment
              entry={entry}
              key={`${entry.actionType}-${entry.id}`}
            />
          ) : (
            <ChangelogUpdate
              entry={entry}
              key={`${entry.actionType}-${entry.id}`}
            />
          ),
        )}
      </Box>
    </ModalSection>
  );
};

export default ChangelogSection;
